import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CalendarClock, Plus, Trash2, CheckCircle2, Circle, Mic2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLOR = {
  interview: "text-[var(--aria-primary)] border-[var(--aria-primary)]/40 bg-[var(--aria-primary)]/10",
  exam: "text-red-400 border-red-400/40 bg-red-400/10",
  assignment: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  deadline: "text-slate-400 border-slate-400/40 bg-slate-400/10",
  meeting: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  drill: "text-[var(--aria-accent)] border-[var(--aria-accent)]/40 bg-[var(--aria-accent)]/10",
};

export default function Calendar() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("deadline");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [showDone, setShowDone] = useState(false);
  const [starting, setStarting] = useState(null);

  const load = async () => { try { const { data } = await api.get("/deadlines"); setItems(data); } catch (err) { console.error("[ARIA]", err); } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim() || !dueDate) { toast.error("Title and date are required"); return; }
    try {
      await api.post("/deadlines", { title, type, due_date: new Date(dueDate).toISOString(), notes });
      setTitle(""); setDueDate(""); setNotes(""); setCreating(false);
      load();
    } catch { toast.error("Failed to add"); }
  };

  const remove = async (id) => { await api.delete(`/deadlines/${id}`); load(); };
  const toggleDone = async (id) => { try { await api.post(`/deadlines/${id}/toggle`); load(); } catch (err) { console.error("[ARIA]", err); } };
  const drillNow = async (d) => {
    setStarting(d.deadline_id);
    try {
      const { data } = await api.post("/interviews", { target: `Review: ${d.concept}`, topic_focus: [d.concept] });
      navigate(`/app/interviews/${data.interview_id}`);
    } catch { toast.error("Could not start drill"); setStarting(null); }
  };

  const visible = items.filter(d => showDone || !d.done);
  const drillCount = items.filter(d => d.type === "drill" && !d.done).length;

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2"><CalendarClock size={22} className="text-[var(--aria-primary)]" /><h1 className="font-display text-3xl">Calendar</h1></div>
            <p className="text-sm text-[var(--aria-text-muted)] mt-1">Deadlines, interviews, and exams.</p>
          </div>
          <button onClick={() => setCreating(!creating)} data-testid="new-deadline-button"
            className="btn btn-primary px-4 py-2 text-sm flex items-center gap-2"><Plus size={14} /> Add</button>
        </div>

        {creating && (
          <div className="aria-card p-5 mb-6 space-y-3" data-testid="deadline-form">
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title"
              data-testid="deadline-title-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
            <div className="grid sm:grid-cols-2 gap-3">
              <select value={type} onChange={e => setType(e.target.value)} data-testid="deadline-type-select"
                className="px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none text-sm">
                {["deadline", "interview", "exam", "assignment", "meeting", "drill"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="datetime-local" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="deadline-date-input"
                className="px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none text-sm" />
            </div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)"
              data-testid="deadline-notes-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="px-4 py-2 text-sm rounded-md border border-[var(--aria-border)]">Cancel</button>
              <button onClick={create} data-testid="deadline-save-button" className="btn btn-primary px-4 py-2 text-sm">Save</button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-3 text-xs text-[var(--aria-text-muted)]">
          <span data-testid="drill-count">{drillCount} spaced re-drill reminder{drillCount === 1 ? "" : "s"} pending — created automatically from interview debriefs.</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} data-testid="show-done-toggle" /> Show completed
          </label>
        </div>

        <div className="space-y-3">
          {visible.map(d => (
            <div key={d.deadline_id} className={`aria-card p-4 flex items-center justify-between gap-3 ${d.done ? "opacity-50" : ""}`} data-testid={`deadline-card-${d.deadline_id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {d.type === "drill" && (
                  <button onClick={() => toggleDone(d.deadline_id)} data-testid={`deadline-done-${d.deadline_id}`} className="text-[var(--aria-text-muted)] hover:text-emerald-400 shrink-0">
                    {d.done ? <CheckCircle2 size={18} className="text-emerald-400" /> : <Circle size={18} />}
                  </button>
                )}
                <span className={`aria-pill text-xs shrink-0 ${TYPE_COLOR[d.type] || ""}`}>{d.type}</span>
                <div className="min-w-0">
                  <div className={`font-medium text-sm truncate ${d.done ? "line-through" : ""}`}>{d.title}</div>
                  <div className="text-xs text-[var(--aria-text-muted)]">{new Date(d.due_date).toLocaleString()}{d.notes ? ` · ${d.notes}` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {d.type === "drill" && !d.done && (
                  <button onClick={() => drillNow(d)} disabled={starting === d.deadline_id} data-testid={`deadline-drill-now-${d.deadline_id}`}
                    className="text-xs text-[var(--aria-primary)] hover:underline inline-flex items-center gap-1">
                    {starting === d.deadline_id ? <Loader2 size={12} className="animate-spin" /> : <Mic2 size={12} />} Drill now
                  </button>
                )}
                <button onClick={() => remove(d.deadline_id)} data-testid={`deadline-delete-${d.deadline_id}`} className="text-[var(--aria-text-muted)] hover:text-[var(--aria-danger)]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {visible.length === 0 && !creating && (
            <div className="text-center py-16 text-[var(--aria-text-muted)]">Nothing scheduled — add your first deadline.</div>
          )}
        </div>
      </div>
    </div>
  );
}
